









































































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import List from '@/components/util/List.vue';
import {
  Asset,
  AssetsApi,
  ApiAssetsGetRequest,
  PerformoSettings,
  Property,
  AssetPagedCollectionApiResponse
} from 'performo-api';
import { mapGetters } from 'vuex';
import { User } from 'oidc-client';

import { SortChangedEvent } from '@/models/SortChangedEvent';
import { ColumnDisplayOption } from '@/models/ColumnDisplayOption';
import { handleApiError } from '@/ApiHelpers';
import AssetClass1Picker from '@/components/system/AssetClass1Picker.vue';
import AssetClass2Picker from '@/components/system/AssetClass2Picker.vue';
import AssetClass3Picker from '@/components/system/AssetClass3Picker.vue';
import Location2Picker from '@/components/system/Location2Picker.vue';

@Component({
  components: {
    List,
    AssetClass1Picker,
    AssetClass2Picker,
    AssetClass3Picker,
    Location2Picker
  },
  computed: mapGetters({
    currentPropertyId: 'currentPropertyId',
    currentUser: 'currentUser',
    api: 'assetsApi',
    settings: 'settings',
    currentProperty: 'currentProperty'
  })
})
export default class AssetList extends Vue {
  @Prop({ required: false }) columnDisplay!: ColumnDisplayOption[];

  private currentPropertyId!: string;
  private currentUser!: User;
  private currentProperty!: Property;
  private settings!: PerformoSettings;
  private api!: AssetsApi;

  private pagedAssets!: AssetPagedCollectionApiResponse;
  private query!: ApiAssetsGetRequest;
  private queryChangedFromPagination = false;

  get columnDisplayComputed(): ColumnDisplayOption[] {
    // if we have a columnDisplay prop, use that, its overriding the system default for a reason.
    if (this.columnDisplay) {
      return this.columnDisplay;
    }
    // finally if we have a system column set, convert to column option and use that.
    const systemColumnDisplay = this.settings
      .assetListColumnOptions as ColumnDisplayOption[];
    if (systemColumnDisplay && systemColumnDisplay.length) {
      return systemColumnDisplay;
    }
    return [
      { displayName: 'Code', columnName: 'assetCode', width: 'auto' },
      {
        displayName: this.settings.multiPropLabelSingular || 'Property',
        columnName: 'propertyId',
        width: 'auto'
      },
      { displayName: 'Name', columnName: 'name', width: '250px' },
      { displayName: 'System', columnName: 'type', width: 'auto' },
      { displayName: 'Category', columnName: 'subtype', width: 'auto' },
      { displayName: 'Function', columnName: '_function', width: 'auto' },
      {
        displayName: this.currentProperty.location1Label || 'Building',
        columnName: 'location1',
        width: 'auto'
      },
      {
        displayName: this.floorLabel,
        columnName: 'floor',
        width: '70px'
      },
      {
        displayName: this.currentProperty.location2Label || 'Room',
        columnName: 'location2',
        width: 'auto'
      }
    ];
  }

  get assets(): Asset[] {
    if (this.pagedAssets) {
      return this.pagedAssets.data || [];
    }
    return [];
  }

  get floorLabel() {
    return this.currentProperty.floorLabel
      ? this.currentProperty.floorLabel
      : 'Floor';
  }

  data() {
    // this lets the undefined query! refs become reactive
    // https://class-component.vuejs.org/guide/class-component.html#data
    return {
      query: undefined,
      pagedAssets: undefined
    };
  }

  private async sortChangedHandler(sort: SortChangedEvent) {
    this.query.sortColumn = sort.column;
    this.query.sortDirection = sort.direction === 1 ? 'ascend' : 'descend';

    this.query.skip = 0;
    await this.updateRouteQuery();
  }

  private async paginationChangeHandler(pageNumber: number) {
    this.query.skip = (this.query.take || 25) * (pageNumber - 1) + 1;
    this.queryChangedFromPagination = true;
    await this.updateRouteQuery();
  }

  async onAssetCodeScannedHandler(barcode: string) {
    this.query.assetCode = barcode;
    await this.updateRouteQuery();
  }

  async onBarcodeScannedHandler(barcode: string) {
    this.query.barcode = barcode;
    await this.updateRouteQuery();
  }

  private handleFilterReset() {
    this.query = {
      status: 'In Service',
      barcode: ''
    };
  }

  @Watch('currentPropertyId')
  private async propertyChanged() {
    await this.updateRouteQuery();
  }

  @Watch('query', { deep: true })
  private async updateRouteQuery() {
    const query = {
      ...this.query,
      location1:
        this.query.campus && this.query.campus !== this.$route.query.campus
          ? ''
          : this.query.location1,
      propertyId: this.currentPropertyId,
      skip: this.queryChangedFromPagination ? this.query.skip : 0,
      take: this.query.take ? this.query.take : 25,
      sortColumn: this.query.sortColumn !== '' ? this.query.sortColumn : '',
      sortDirection:
        this.query.sortDirection !== '' ? this.query.sortDirection : 'ascend'
    };
    // update the route list if its not locked
    if (Object.keys(this.$route.query).length > 0) {
      await this.$router
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .push({ name: 'assets', query: query as any })
        .catch(() => {
          /*empty*/
        });
    } else {
      await this.$router
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .replace({ name: 'assets', query: query as any })
        .catch(() => {
          /*empty*/
        });
    }
    this.queryChangedFromPagination = false;
  }

  @Watch('$route.query', { deep: true })
  private async queryChanged(val: ApiAssetsGetRequest) {
    if (val) {
      await this.fetchAssets();
    }
  }

  private async fetchAssets() {
    const query = Object.assign({}, this.$route.query);
    try {
      this.pagedAssets = await this.api.apiAssetsGet(query);
    } catch (error) {
      handleApiError(error);
    }
  }

  private assetClickedHandler(asset: Asset) {
    if (asset.assetId) {
      this.$router.push({
        name: 'asset-detail',
        params: { id: asset.assetId.toString() }
      });
    }
  }

  private async mounted() {
    if (Object.keys(this.$route.query).length > 0) {
      this.query = Object.assign({}, this.$route.query);
      await this.fetchAssets();
    } else {
      this.query = {
        status: 'In Service',
        barcode: ''
      };
    }
  }
}
